import React from 'react';
import type { PaymentRun, RecordResult } from '../../lib/paymentRunsApi';
import { paymentRunMessage } from '../../lib/paymentRunsApi';
import { formatReportMoney, supplierLabel } from '../accountsPayableReports/format';

/**
 * What "Record payments" did, supplier by supplier: which were recorded and which were not, and
 * for each that was not, why (in words chosen from its code) and that nothing was paid to it.
 * Each supplier is its own all-or-nothing, so one failing never undoes another.
 */
export interface RecordOutcomePanelProps {
  run: PaymentRun;
  results: RecordResult[];
  onDismiss: () => void;
}

export const RecordOutcomePanel: React.FC<RecordOutcomePanelProps> = ({ run, results, onDismiss }) => {
  const groupOf = (id: string) => run.groups.find((g) => g.id === id);
  const recorded = results.filter((r) => r.status === 'recorded');
  const failed = results.filter((r) => r.status === 'failed');

  return (
    <section
      aria-label="What was recorded"
      className={`rounded-lg border p-3 ${failed.length ? 'border-amber-300 bg-amber-50' : 'border-emerald-300 bg-emerald-50'}`}
      data-testid="record-outcome"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">
            {failed.length
              ? `${recorded.length} of ${results.length} payments recorded`
              : `All ${recorded.length} ${recorded.length === 1 ? 'payment is' : 'payments are'} recorded`}
          </h2>
          {failed.length ? (
            <p className="mt-0.5 text-sm text-slate-700">
              The ones below were not recorded, and nothing was paid to those suppliers. Fix them here, save, and record
              again: only the ones not recorded are tried, so no one is paid twice.
            </p>
          ) : null}
        </div>
        <button type="button" onClick={onDismiss} className="text-xs text-slate-600 hover:underline">
          Dismiss
        </button>
      </div>

      {failed.length ? (
        <ul className="mt-2 space-y-1 text-sm" aria-label="Payments not recorded">
          {failed.map((r) => {
            const g = groupOf(r.groupId);
            return (
              <li key={r.groupId} className="text-red-800">
                <span className="font-medium">
                  {g ? `${supplierLabel(g.supplierName)} (${formatReportMoney(g.amount, g.currency)})` : 'A supplier'}
                </span>
                : {paymentRunMessage(r.errorCode)}
              </li>
            );
          })}
        </ul>
      ) : null}

      {recorded.length ? (
        <ul className="mt-2 space-y-1 text-sm" aria-label="Payments recorded">
          {recorded.map((r) => {
            const g = groupOf(r.groupId);
            return (
              <li key={r.groupId} className="text-emerald-900">
                {g ? `${supplierLabel(g.supplierName)}: ${formatReportMoney(g.amount, g.currency)}, reference ${g.reference}` : 'A supplier'}
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
};

export default RecordOutcomePanel;
